import { Component, computed, inject, input, model, signal, viewChild } from '@angular/core';
import { MatAccordion, MatExpansionPanel } from '@angular/material/expansion';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { StExpansionPanelComponent } from '@src/suite/common/components/st-expansion-panel/st-expansion-panel.component';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { LanguageSettings } from '@src/suite/common/services/language-settings';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';

import { PSTFieldNames } from '../../data-models/fields/pst-field-names';

@Component({ template: '' })
export class StFullScreenComponent {
    public visualStateService = inject(VisualStateService);
    public store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    languageSettingsOld = inject(LanguageSettings);
    readonly data = model<StorytellingTopicRecord | StorytellingAssignmentRecord | StorytellingStoryRecord>();

    languageSettings = this.store.selectSignal(selectI18N);

    readonly accordion = viewChild<MatAccordion>(MatAccordion);

    public readonly sidecarMenu = input<FullscreenLayoutAreas>();
    protected readonly expandedGroup = signal<string>('');
    protected readonly sidecar = computed(() => {
        return this.sidecarMenu() ? `${this.sidecarMenu()}-` : `${FullscreenLayoutAreas.MAIN}-`;
    });

    readonly collapseAllLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebCollapseAllLabel))();
    });

    readonly expandAllLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebExpandAllLabel))();
    });

    readonly expandedState = computed(() => {
        return this.visualStateService.expanderSettingsMap()?.[this.expandedGroup()] || {};
    });

    readonly groups = computed(() => {
        if (this.data()) {
            return this.globalTablesService.getAllPropertyGroupsFor(this.getType()).map(group => {
                const fieldGroupLabel = 'PSTWebFieldGroup';
                const fieldGroupName = group.name === '' ? 'Properties' : group.name;
                const groupLabel = this.languageSettings()[`${fieldGroupLabel}_${fieldGroupName}`] != null ? this.languageSettings()[`${fieldGroupLabel}_${fieldGroupName}`] : fieldGroupName;

                return {
                    ...group,
                    label: groupLabel,
                    items: this.globalTablesService.getDynamicPropertiesFor(this.getType(), group.name).filter(p => p.ID !== PSTFieldNames.PSTTeamLocation),
                };
            });
        }
        return [];
    });

    readonly getType = computed(() => {
        return 'getType method must be implemented in derived class';
    });

    // TODO: FIX THIS
    readonly attachmentsFieldGroupLabel = computed(() => {
        const attachmentsFieldGroupLabel = 'PSTWebFieldGroup';

        return this.store
            .selectSignal(selectTranslatedKey(`${attachmentsFieldGroupLabel}_${I18nKeys.PSTWebAttachmentsPane}`))()
            .includes(attachmentsFieldGroupLabel)
            ? this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebAttachmentsPane))()
            : this.store.selectSignal(selectTranslatedKey(`${attachmentsFieldGroupLabel}_${I18nKeys.PSTWebAttachmentsPane}`))();
    });

    panelStateChanged($event: boolean, title: string | undefined) {
        this.visualStateService.saveExpanderSettings(this.expandedGroup(), `${this.sidecar()}${title}`, $event);
    }

    expandCollapseAll(paneContainer: StExpansionPanelComponent | MatExpansionPanel) {
        if (paneContainer.expanded) {
            this.accordion().closeAll();
        } else {
            this.accordion().openAll();
        }
    }
}
